import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { Box, Button, Typography } from '@mui/material'
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useStyles } from '../style'
import SkillChip from './skill-chip'

/**
 * Given the vertical offset (offsetTop) of each chip in DOM order, return how
 * many chips fall within the first `previewRows` visual rows. Chips fill the
 * flex-wrap container row by row, so every chip whose top is within the allowed
 * rows is part of the preview. Always returns at least 1 so a group never
 * collapses to nothing.
 */
export const getVisibleCountForRows = (tops, previewRows = 1) => {
  if (!tops.length) return 0

  const distinctTops = []
  tops.forEach(top => {
    if (!distinctTops.includes(top)) distinctTops.push(top)
  })
  distinctTops.sort((a, b) => a - b)

  if (previewRows >= distinctTops.length) return tops.length

  const maxAllowedTop = distinctTops[previewRows - 1]
  const count = tops.filter(top => top <= maxAllowedTop).length
  return Math.max(1, count)
}

const CategoryGroup = ({
  group,
  selectedSkills,
  onToggleSkill,
  previewRows = 1,
  forceExpanded = false,
  activeSk = null,
  onChipFocus
}) => {
  const classes = useStyles()
  const [expanded, setExpanded] = useState(false)
  // Number of chips that fit in the first `previewRows` rows. null = not yet
  // measured / needs (re)measuring against a full render of every chip.
  const [clampCount, setClampCount] = useState(null)
  const chipsRef = useRef(null)
  const lastWidthRef = useRef(0)

  const children = useMemo(() => group.children || [], [group.children])
  const childrenKey = useMemo(() => children.map(child => child.sk).join('|'), [children])
  const selectedInGroup = children.filter(child => selectedSkills.includes(child.sk)).length

  const isExpanded = forceExpanded || expanded
  const visibleChildren = isExpanded || clampCount === null
    ? children
    : children.slice(0, clampCount)

  // When the set of chips changes, drop the previous measurement so the next
  // pass measures a full render rather than an already-clamped slice.
  useLayoutEffect(() => {
    setClampCount(null)
  }, [childrenKey])

  // Measure the full render (clampCount === null) and clamp to the first rows.
  useLayoutEffect(() => {
    if (isExpanded || clampCount !== null) return
    const container = chipsRef.current
    if (!container) return

    const tops = Array.from(container.children).map(el => el.offsetTop)
    lastWidthRef.current = container.offsetWidth
    setClampCount(getVisibleCountForRows(tops, previewRows))
  }, [childrenKey, isExpanded, clampCount, previewRows])

  // Re-measure when the container width changes (responsive reflow). Height
  // changes from clamping itself are ignored to avoid a measure/clamp loop.
  useLayoutEffect(() => {
    const container = chipsRef.current
    if (!container || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(entries => {
      const width = entries[0].contentRect.width
      if (width !== lastWidthRef.current) {
        lastWidthRef.current = width
        setClampCount(null)
      }
    })
    observer.observe(container)
    return () => observer.disconnect()
  }, [])

  const hasOverflow = clampCount !== null && clampCount < children.length
  const showToggle = !forceExpanded && (expanded || hasOverflow)
  const remaining = children.length - (clampCount ?? children.length)

  const groupSlug = group.parentName.replace(/\s+/g, '-').toLowerCase()

  return (
    <Box
      className={classes.categoryGroup}
      data-test={`category-group-${groupSlug}`}
      role='group'
      aria-label={`${group.parentName}, ${selectedInGroup} of ${children.length} selected`}
    >
      <Box className={classes.categoryGroupHeader}>
        <Typography
          className={classes.categoryGroupTitle}
          component='h3'
          data-test={`category-group-title-${groupSlug}`}
        >
          {group.parentName}
        </Typography>
      </Box>

      <Box className={classes.categoryGroupChips} ref={chipsRef}>
        {visibleChildren.map((child) => (
          <SkillChip
            key={child.sk}
            skill={child}
            isSelected={selectedSkills.includes(child.sk)}
            onClick={() => onToggleSkill(child.sk)}
            tabIndex={child.sk === activeSk ? 0 : -1}
            onFocus={() => onChipFocus && onChipFocus(child.sk)}
          />
        ))}
      </Box>

      {showToggle && (
        <Button
          className={classes.seeMoreButton}
          onClick={() => setExpanded(prev => !prev)}
          endIcon={expanded ? <ExpandLess fontSize='small' /> : <ExpandMore fontSize='small' />}
          aria-expanded={expanded}
          disableRipple
          data-test={`category-group-toggle-${groupSlug}`}
        >
          {expanded ? `See less on ${group.parentName}` : `See more on ${group.parentName} (${remaining})`}
        </Button>
      )}
    </Box>
  )
}

export default CategoryGroup
